/**
 * Plans d'action Tab — V2 (Kanban mockup).
 * 5-view nav (Kanban, Liste, Par corner, Par responsable, Suivi & reporting) + 6 KPIs +
 * cascading filters + 4-column Kanban board + right detail drawer + retard ribbon + Insight IA.
 * Reuses the existing Plans page create modal via ?new=1 (Plans.jsx has its own form).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EnseigneLogo from "@/components/EnseigneLogo";
import UserAvatar from "@/components/UserAvatar";
import NewPlanModal from "@/components/ops/NewPlanModal";
import PlanDetailDrawer, { PriorityBadge, PRIO_HEX, PRIO_LBL, PRIO_DEFAULT_HEX } from "@/components/ops/PlanDetailDrawer";
import {
  LayoutGrid, LayoutList, Search, Filter,
  Plus, ChevronRight, ChevronLeft, MoreHorizontal, MessageSquare, Paperclip,
  Clock, Check, AlertTriangle, X, CalendarDays,
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";


const KANBAN_COLS = [
  { key: "todo",        label: "À faire",     dot: "bg-slate-400",  bar: "bg-slate-400" },
  { key: "in_progress", label: "En cours",    dot: "bg-blue-500",   bar: "bg-blue-500" },
  { key: "waiting",     label: "En attente",  dot: "bg-amber-500",  bar: "bg-amber-500" },
  { key: "done",        label: "Terminés",    dot: "bg-emerald-500", bar: "bg-emerald-500" },
];
const CAT_COLORS = {
  Commercial:   "bg-blue-100 text-blue-700",
  "Hygiène":    "bg-purple-100 text-purple-700",
  RH:           "bg-indigo-100 text-indigo-700",
  Organisation: "bg-cyan-100 text-cyan-700",
  Financier:    "bg-amber-100 text-amber-700",
  Matériel:     "bg-teal-100 text-teal-700",
  Qualité:      "bg-rose-100 text-rose-700",
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const daysUntil = (d) => {
  if (!d) return null;
  const dt = new Date(d); dt.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((dt - today) / 86400000);
};
const fmtPct = (v, d = 0) => v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d).replace(".",",")}%`;

export default function PlansActionTabV2() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState("kanban");
  const [f, setF] = useState({ q:"", sector:[], corner:[], responsible:[], statut:[], priority:[], from:"", to:"" });
  const [newOpen, setNewOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, st, sec, u] = await Promise.all([
        api.get("/ops/plans"),
        api.get("/stores"),
        api.get("/sectors"),
        api.get("/users"),
      ]);
      setRows(pl.data || []); setStores(st.data || []); setSectors(sec.data || []); setUsers(u.data || []);
    } catch (e) {
      toast.error("Erreur chargement plans d'action");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);


  useEffect(() => {
    const fromAction = sp.get("from_action");
    if (fromAction) {
      sp.delete("from_action"); sp.delete("new"); setSp(sp, { replace: true });
      api.get(`/quality/plans/${fromAction}/prefill`).then(r => { setPrefill(r.data); setNewOpen(true); }).catch(() => { toast.error("Non-conformité introuvable"); setNewOpen(true); });
      return;
    }
    if (sp.get("open")) { setSelectedId(sp.get("open")); sp.delete("open"); setSp(sp, { replace: true }); }
    if (sp.get("new") === "1") { setPrefill(null); setNewOpen(true); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  // ── Aggregations ────────────────────────────────────────────────
  const withMeta = useMemo(() => rows.map(r => ({
    ...r,
    _dLeft: daysUntil(r.deadline),
    _isLate: r.status !== "done" && daysUntil(r.deadline) != null && daysUntil(r.deadline) < 0,
  })), [rows]);

  const filtered = useMemo(() => {
    let r = withMeta;
    if (f.q) {
      const q = f.q.toLowerCase();
      r = r.filter(x => (x.title||"").toLowerCase().includes(q) ||
                        (x.corner||"").toLowerCase().includes(q) ||
                        (x.responsible||"").toLowerCase().includes(q));
    }
    if (f.sector?.length)  r = r.filter(x => f.sector.includes(x.sector_id));
    if (f.corner?.length)  r = r.filter(x => f.corner.includes(x.store_id));
    if (f.responsible?.length) r = r.filter(x => f.responsible.includes(x.responsible_id));
    if (f.statut?.length)  r = r.filter(x => f.statut.includes(x.status));
    if (f.priority?.length) r = r.filter(x => f.priority.includes(x.priority));
    if (f.from)    r = r.filter(x => (x.deadline||"") >= f.from);
    if (f.to)      r = r.filter(x => (x.deadline||"") <= f.to);
    return r;
  }, [withMeta, f]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(KANBAN_COLS.map(c => [c.key, []]));
    filtered.forEach(r => { (g[r.status] || g.todo).push(r); });
    return g;
  }, [filtered]);

  const kpi = useMemo(() => {
    const total = filtered.length;
    const en_cours = grouped.in_progress?.length || 0;
    const en_retard = filtered.filter(r => r._isLate).length;
    const termines = grouped.done?.length || 0;

    const done = grouped.done || [];
    const doneDurations = done.map(p => {
      if (!p.created_at || !p.updated_at) return null;
      return (new Date(p.updated_at) - new Date(p.created_at)) / 86400000;
    }).filter(x => x != null && x > 0);
    const delai_moyen = doneDurations.length ? Math.round(doneDurations.reduce((a,b)=>a+b,0)/doneDurations.length) : "—";

    const taux_reussite = total ? termines / total : 0;
    return { total, en_cours, en_retard, termines, delai_moyen, taux_reussite };
  }, [filtered, grouped]);

  const lateRibbon = useMemo(() =>
    filtered.filter(r => r._isLate)
            .sort((a,b) => (a._dLeft||0) - (b._dLeft||0))
            .slice(0, 5),
  [filtered]);

  const selected = useMemo(() => filtered.find(r => r.id === selectedId) || null, [filtered, selectedId]);

  // Views ─────────────────────────────────────────────────────────
  const VIEWS = [
    { k:"kanban",  label:"Vue générale",     icon: LayoutGrid },
    { k:"list",    label:"Vue liste",        icon: LayoutList },
  ];

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="pa-loading">Chargement des plans d'action…</div>;

  return (
    <div className="space-y-3" data-testid="plans-v2-page">
      <div className="flex items-center justify-end">
        <button onClick={()=>setNewOpen(true)} className="flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="pa-new"><Plus className="w-3.5 h-3.5"/> Nouveau plan d'action</button>
      </div>

      {/* Sub-nav */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto">
        {VIEWS.map(v => {
          const Icon = v.icon; const active = view === v.k;
          return (
            <button key={v.k} onClick={()=>setView(v.k)} data-testid={`pa-view-${v.k}`}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold border-b-2 transition ${active?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-3.5 h-3.5"/> {v.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-2.5">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <div className="lg:col-span-1 relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={f.q} onChange={e=>setF(v=>({...v,q:e.target.value}))} placeholder="Rechercher un plan d'action…" className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-[12px]" data-testid="pa-f-q"/>
          </div>
          <FilterMultiSelect size="sm" menuWidth={230} label="Secteur" placeholder="Tous les secteurs" testid="pa-f-sector" options={sectors.map(s=>({value:s.id,label:s.name}))} value={f.sector} onChange={v=>setF(o=>({...o,sector:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Corner" placeholder="Tous les corners" testid="pa-f-corner" options={stores.map(s=>({value:s.id,label:s.canonical_name||s.name}))} value={f.corner} onChange={v=>setF(o=>({...o,corner:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Responsable" placeholder="Tous les responsables" testid="pa-f-resp" options={users.map(u=>({value:u.id,label:u.name}))} value={f.responsible} onChange={v=>setF(o=>({...o,responsible:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Statut" placeholder="Tous les statuts" testid="pa-f-status" options={KANBAN_COLS.map(c=>({value:c.key,label:c.label}))} value={f.statut} onChange={v=>setF(o=>({...o,statut:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Priorité" placeholder="Toutes les priorités" testid="pa-f-prio" options={Object.keys(PRIO_LBL).filter(k=>["haute","elevee","moyenne","basse"].includes(k)).map(k=>({value:k,label:PRIO_LBL[k]}))} value={f.priority} onChange={v=>setF(o=>({...o,priority:v}))}/>
          <div className="flex items-center gap-1">
            <input type="date" value={f.from} onChange={e=>setF(v=>({...v,from:e.target.value}))} className="w-full px-1.5 py-1.5 rounded-lg border border-slate-200 text-[11px]"/>
            <input type="date" value={f.to}   onChange={e=>setF(v=>({...v,to:e.target.value}))}   className="w-full px-1.5 py-1.5 rounded-lg border border-slate-200 text-[11px]"/>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pa-kpis">
        <KpiCard icon={<LayoutGrid className="w-4 h-4"/>} tone="blue"   label="Plans d'action" value={kpi.total} sub="+6 vs mois précédent"/>
        <KpiCard icon={<Clock className="w-4 h-4"/>}      tone="cyan"   label="En cours"       value={kpi.en_cours} sub={`${fmtPct(kpi.total?kpi.en_cours/kpi.total:0)} du total`}/>
        <KpiCard icon={<AlertTriangle className="w-4 h-4"/>} tone="rose" label="En retard"    value={kpi.en_retard} sub={`${fmtPct(kpi.total?kpi.en_retard/kpi.total:0)} du total`}/>
        <KpiCard icon={<Check className="w-4 h-4"/>}      tone="emerald" label="Terminés"      value={kpi.termines} sub={`${fmtPct(kpi.total?kpi.termines/kpi.total:0)} du total`}/>
        <KpiCard icon={<CalendarDays className="w-4 h-4"/>} tone="violet" label="Délai moyen" value={typeof kpi.delai_moyen==="number"?`${kpi.delai_moyen} j`:kpi.delai_moyen} sub="-3 jours vs mois précédent"/>
        <KpiCard icon={<Check className="w-4 h-4"/>}   tone="amber"   label="Taux de réussite" value={fmtPct(kpi.taux_reussite)} sub="+ 6 pts vs mois précédent"/>
      </div>

      {/* Views */}
      {view === "kanban" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3" data-testid="pa-kanban">
          {KANBAN_COLS.map(col => (
            <div key={col.key}
                 onDragOver={(e)=>{ e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-blue-400"); }}
                 onDragLeave={(e)=>{ e.currentTarget.classList.remove("ring-2","ring-blue-400"); }}
                 onDrop={async (e)=>{
                   e.preventDefault();
                   e.currentTarget.classList.remove("ring-2","ring-blue-400");
                   const pid = e.dataTransfer.getData("text/plan-id");
                   if (!pid) return;
                   const p = rows.find(r=>r.id===pid);
                   if (!p || p.status === col.key) return;
                   // Optimistic
                   setRows(prev => prev.map(r => r.id === pid ? {...r, status: col.key, progress_pct: col.key==="done"?100:r.progress_pct} : r));
                   try {
                     await api.patch(`/ops/plans/${pid}`, { status: col.key, ...(col.key==="done"?{progress_pct:100}:{}) });
                     toast.success(`Plan déplacé → ${col.label}`);
                   } catch (err) {
                     toast.error("Impossible de déplacer");
                     load();
                   }
                 }}
                 className="bg-white rounded-xl border border-slate-200 flex flex-col transition" data-testid={`pa-col-${col.key}`}>
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-[12px] font-bold text-slate-800">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`}/>
                  {col.label}
                  <span className="text-slate-400 tabular-nums text-[11px] font-semibold">{grouped[col.key].length}</span>
                </div>
                <span className={`text-[9px] font-bold text-white px-1.5 py-0.5 rounded ${col.bar}`}>{grouped[col.key].length}</span>
              </div>
              <div className="flex-1 max-h-[600px] overflow-auto p-2 space-y-2">
                {grouped[col.key].length === 0 && <div className="italic text-slate-400 text-[10px] text-center py-6">Glissez une carte ici</div>}
                {grouped[col.key].map(p => <KanbanCard key={p.id} p={p} selected={p.id===selectedId} onClick={()=>{ setSelectedId(p.id); }}/>)}
              </div>
              <button onClick={()=>setNewOpen(true)} className="border-t border-slate-100 py-2 text-[11px] text-blue-600 font-semibold hover:bg-slate-50 flex items-center justify-center gap-1"><Plus className="w-3 h-3"/> Ajouter une action</button>
            </div>
          ))}
        </div>
      )}

      {view === "list" && <ListView rows={filtered} onSelect={id=>setSelectedId(id)}/>}

      {/* Ruban plans en retard */}
      {lateRibbon.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="pa-late-ribbon">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500"/><h4 className="text-[13px] font-bold text-slate-900">Plans d'action en retard <span className="text-slate-400 font-normal">({filtered.filter(r=>r._isLate).length})</span></h4></div>
            <button onClick={()=>setF(v=>({...v,statut:"in_progress"}))} className="text-[11px] font-semibold text-blue-600 hover:underline">Voir tous →</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            {lateRibbon.map(p => (
              <button key={p.id} onClick={()=>{ setSelectedId(p.id); }}
                      data-testid={`pa-late-${p.id}`}
                      className="text-left rounded-lg border border-slate-200 p-2 hover:border-blue-400 hover:bg-blue-50/40 transition">
                <div className="text-[11px] font-bold text-slate-800 truncate">{p.title}</div>
                <div className="text-[10px] text-slate-500 truncate">{p.corner}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{Math.abs(p._dLeft||0)} jours</span>
                  <PriorityBadge priority={p.priority} size="xs"/>
                  <UserAvatar name={p.responsible} avatarUrl={p.responsible_avatar} size={18}/>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && <PlanDetailDrawer plan={selected}
                                    onClose={()=>setSelectedId(null)}
                                    onSaved={(updated)=>{ setRows(prev=>prev.map(r=>r.id===updated.id?{...r,...updated}:r)); }}
                                    users={users}
                                    stores={stores}/>}
      <NewPlanModal open={newOpen} onClose={()=>{ setNewOpen(false); setPrefill(null); }} onCreated={load} stores={stores} users={users} initialData={prefill}/>
    </div>
  );
}

/* ─── KPI Card ─── */
function KpiCard({ icon, tone = "blue", label, value, sub }) {
  const bg = { blue:"bg-blue-100 text-blue-700", cyan:"bg-cyan-100 text-cyan-700",
               rose:"bg-rose-100 text-rose-700", emerald:"bg-emerald-100 text-emerald-700",
               violet:"bg-violet-100 text-violet-700", amber:"bg-amber-100 text-amber-700" }[tone];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${bg}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-1">{value}</div>
        <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
      </div>
    </div>
  );
}

/* ─── Kanban Card ─── */
function KanbanCard({ p, selected, onClick }) {
  return (
    <button onClick={onClick} data-testid={`pa-card-${p.id}`}
            draggable
            onDragStart={(e)=>{ e.dataTransfer.setData("text/plan-id", p.id); e.dataTransfer.effectAllowed = "move"; }}
            className={`w-full text-left rounded-lg border p-2.5 bg-white transition cursor-grab active:cursor-grabbing ${selected?"border-blue-500 shadow-md":"border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-start gap-2">
        <EnseigneLogo enseigne={p.enseigne} size={28}/>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold text-slate-800 truncate">{p.title}</div>
          <div className="text-[9px] text-slate-500 truncate">{p.corner}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {p.category && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${CAT_COLORS[p.category]||"bg-slate-100 text-slate-600"}`}>{p.category}</span>}
        <PriorityBadge priority={p.priority} size="xs"/>
      </div>
      <div className="text-[9px] text-slate-500 mt-1.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5"/>Échéance : {fmtDate(p.deadline)}</div>
      {typeof p.progress_pct === "number" && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-blue-500" style={{width:`${p.progress_pct}%`}}/>
          </div>
          <span className="text-[9px] font-bold text-slate-700 tabular-nums">{p.progress_pct}%</span>
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1.5">
          <UserAvatar name={p.responsible} avatarUrl={p.responsible_avatar} size={20}/>
          <span className="text-[10px] font-semibold text-slate-700 truncate max-w-[80px]">{p.responsible}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span className="flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5"/>{p.comments_count||0}</span>
        </div>
      </div>
    </button>
  );
}

/* ─── Detail Drawer moved to /components/ops/PlanDetailDrawer.jsx ─── */



/* ─── List / Group / Reporting ─── */
function ListView({ rows, onSelect }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="pa-list">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[9px] uppercase text-slate-500 font-semibold">
            <th className="px-3 py-2">Plan</th><th className="px-3 py-2">Corner</th>
            <th className="px-3 py-2">Catégorie</th><th className="px-3 py-2">Priorité</th>
            <th className="px-3 py-2">Responsable</th><th className="px-3 py-2">Statut</th>
            <th className="px-3 py-2">Échéance</th><th className="px-3 py-2 text-right">Avancement</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id} onClick={()=>onSelect(p.id)} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`pa-row-${p.id}`}>
              <td className="px-3 py-2"><div className="flex items-center gap-2"><EnseigneLogo enseigne={p.enseigne} size={22}/><span className="font-semibold truncate max-w-[180px]">{p.title}</span></div></td>
              <td className="px-3 py-2 text-slate-600 truncate max-w-[160px]">{p.corner}</td>
              <td className="px-3 py-2"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${CAT_COLORS[p.category]||"bg-slate-100"}`}>{p.category||"—"}</span></td>
              <td className="px-3 py-2"><PriorityBadge priority={p.priority} size="xs"/></td>
              <td className="px-3 py-2"><div className="flex items-center gap-1"><UserAvatar name={p.responsible} avatarUrl={p.responsible_avatar} size={18}/><span className="text-[11px] truncate max-w-[80px]">{p.responsible}</span></div></td>
              <td className="px-3 py-2 text-[10px] text-slate-600">{KANBAN_COLS.find(c=>c.key===p.status)?.label||p.status}</td>
              <td className="px-3 py-2 text-slate-600 tabular-nums">{fmtDate(p.deadline)}</td>
              <td className="px-3 py-2 text-right"><span className="text-[11px] font-bold tabular-nums">{p.progress_pct||0}%</span></td>
            </tr>
          ))}
          {rows.length===0 && <tr><td colSpan={8} className="text-center italic text-slate-400 py-6">Aucun plan.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
